/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { Button, Container, Row, Col, Modal } from 'react-bootstrap';
import axios from 'axios';
import JsBarcode from 'jsbarcode';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PencilIcon from '../../static/images/ship.png';
import CustomButton from '../../components/button';
import FormContainer from '../../components/formContainer/index';
import FormField from '../../components/input-field/index';
import { IoPrintSharp } from "react-icons/io5";

const NewShipment = ({ user }) => {
  const initialShipmentData = {
    shipmentId: '',
    senderName: user.name || '', // Default to user.name
    phoneNumber: '+971',
    receiverName: '',
    address: '',
    charges: '0.0', // Default value for charges
  };

  const [shipmentData, setShipmentData] = useState(initialShipmentData);
  const [showModal, setShowModal] = useState(false);

  const generateShipmentId = (email) => {
    const emailHash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const randomNum1 = Math.floor(10000 + Math.random() * 90000);
    const timestamp = new Date().toISOString().replace(/[-:.TZ]/g, '');
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    return `FDS-${randomNum1}-${randomNum}`;
  };

  useEffect(() => {
    const id = generateShipmentId(user.email);
    setShipmentData((prev) => ({ ...prev, shipmentId: id }));
    generateBarcode(id);
  }, [user.email]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setShipmentData({ ...shipmentData, [name]: value });
  };

  const generateBarcode = (id) => {
    const barcodeElement = document.getElementById('barcode');
    if (barcodeElement) {
      JsBarcode(barcodeElement, id || shipmentData.shipmentId, {
        format: 'CODE128',
        width: 1,
        height: 50,
        displayValue: true,
      });
    }
  };

  const validateForm = () => {
    for (const key in shipmentData) {
      if (!shipmentData[key] && key !== 'charges') {
        return false;
      }
    }
    return true;
  };

  const handleSaveToDB = async () => {
    try {
      const email = user.email;
      // Update the phoneNumber field directly in shipmentData to exclude '+'
      const updatedShipmentData = {
        ...shipmentData,
        phoneNumber: shipmentData.phoneNumber.startsWith('+')
          ? shipmentData.phoneNumber.slice(1)
          : shipmentData.phoneNumber,
      };
      const response = await axios.post(
        `${process.env.REACT_APP_DEV_BACKEND_URL}/shipment/create`,
        { ...updatedShipmentData, email },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      toast.success('Shipment submitted successfully!');
      return response.data;
    } catch (error) {
      console.error('Error saving shipment to database:', error);
      toast.error('Failed to save shipment. Please try again.');
      return false;
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Shipment Details</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 5px;
              padding: 5px;
            }
            .container {
              display: flex;
              flex-direction: column;
              align-items: center;
            }
            h1 {
              text-align: center;
            }
            span {
              font-size: 10px;
            }
            table {
              width: 90%;
              border-collapse: collapse;
              font-size: 10px;
            }
            td, th {
              border: 1px solid #ddd;
              padding: 4px;
            }
            td {
              text-align: left;
            }
            .barcode-container {
              display: flex;
              justify-content: center;
            }
            #printBarcode {
              width: 600px;
              height: 50px;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <table>
              <tr><td>Shipment ID</td><td>${shipmentData.shipmentId}</td></tr>
              <tr><td>Sender</td><td>${shipmentData.senderName}</td></tr>
              <tr><td>Receiver Name</td><td>${shipmentData.receiverName}</td></tr>
              <tr><td>Phone Number</td><td>${shipmentData.phoneNumber}</td></tr>
              <tr><td>Address</td><td>${shipmentData.address}</td></tr>
              <tr><td>Charges</td><td>${shipmentData.charges} AED</td></tr>
              <tr><td>Date</td><td>${new Date().toLocaleString('en-GB', { timeZone: 'Asia/Dubai' })}</td></tr>
            </table>
            <span>www.falcondelivery.ae</span>
            <div class="barcode-container">
              <svg id="printBarcode"></svg>
            </div>
          </div>
        </body>
      </html>
    `);

    const printBarcode = printWindow.document.getElementById('printBarcode');
    if (printBarcode) {
      JsBarcode(printBarcode, shipmentData.shipmentId, {
        format: 'CODE128',
        width: 1,
        height: 50,
        displayValue: true,
      });
    }

    printWindow.document.close();
    printWindow.print();
  };

  const handleModalConfirm = async () => {
    setShowModal(false);
    const isSaved = await handleSaveToDB();
    if (isSaved) {
      handlePrint();
      const newId = generateShipmentId(user.email);
      setShipmentData({ ...initialShipmentData, shipmentId: newId });
      generateBarcode(newId);
    }
  };

  const handleModalClose = () => setShowModal(false);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fill all fields before submitting.');
      return;
    }
    setShowModal(true);
  };

  return (
    <Container fluid className="p-4 bg-light">
      <FormContainer>
        <div className="text-center">
          <img
            className="img-fluid"
            alt="Logo"
            src={PencilIcon}
            width="100"
            height="100"
          />
        </div>
        <ToastContainer />
        <h2 className="mb-2 mt-1 ml-2 fw-bold" style={{ color: '#005A9C' }}>
          Create New Shipment
        </h2>
        <br/><br/>
        <form onSubmit={handleFormSubmit}>
          <label>Sender: {shipmentData.senderName}</label><br/><br/>
          <FormField
            label="Receiver Name"
            controlId="receiverName"
            name="receiverName"
            type="text"
            placeholder="Enter Receiver Name"
            value={shipmentData.receiverName}
            onChange={handleChange}
          />
          <FormField
            label="Phone Number"
            controlId="phoneNumber"
            name="phoneNumber"
            type="text"
            placeholder="+971XXXXXXXXX"
            value={shipmentData.phoneNumber}
            onChange={handleChange}
          />
          <FormField
            label="Address"
            controlId="address"
            name="address"
            type="text"
            placeholder="Enter Address"
            value={shipmentData.address}
            onChange={handleChange}
          />
          {/* <FormField
            label="Weight (kg)"
            controlId="weight"
            name="weight"
            type="number"
            placeholder="Enter Weight"
            step="any"
            required={false}
            value={shipmentData.weight}
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              if (value <= 0) {
                toast.error('Weight must be greater than 0.');
                return;
              }
              handleChange(e);
            }}
          /> */}
          <FormField
            label="Charges (AED)"
            controlId="charges"
            name="charges"
            type="number"
            placeholder="Enter Charges"
            step="any"
            value={shipmentData.charges}
            onChange={handleChange}
          />
          <Row className="mt-3">
            <Col className="d-flex justify-content-end">
              <CustomButton type="submit" variant="primary" className="mr-2">
                <IoPrintSharp />
                <div className="ml-3">Submit & Print Label</div>
              </CustomButton>
            </Col>
          </Row>
        </form>
        <div className="mt-2 d-flex flex-col ">
          <h5 className="ml-3">Generated Barcode:</h5>
          <svg id="barcode"></svg>
        </div>

        <Modal show={showModal} onHide={handleModalClose} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Submission</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to submit and print the shipment details?
          </Modal.Body>
          <Modal.Footer>
            <CustomButton variant="secondary" onClick={handleModalClose}>
              Cancel
            </CustomButton>
            <CustomButton variant="primary" onClick={handleModalConfirm}>
              Confirm
            </CustomButton>
          </Modal.Footer>
        </Modal>
      </FormContainer>
    </Container>
  );
};

export default NewShipment;
